import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Store, Menu, X, ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "../cart/CartContext";

const LINKS = [
  { label: "Our Oils", to: "/#oils", testid: "nav-link-oils" },
  { label: "Our Process", to: "/process", testid: "nav-link-process" },
  { label: "Our Story", to: "/about", testid: "nav-link-story" },
  { label: "Journal", to: "/journal", testid: "nav-link-journal" },
];

export const scrollToHash = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -104 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { count } = useCart();

  const go = (to) => {
    const wasOpen = open;
    setOpen(false);
    const delay = wasOpen ? 250 : 0;
    if (to.startsWith("/#")) {
      const hash = to.slice(1);
      if (location.pathname === "/") {
        setTimeout(() => scrollToHash(hash), delay);
      } else {
        sessionStorage.setItem("scrollToHash", hash);
        navigate("/");
      }
    } else {
      setTimeout(() => navigate(to), delay);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 bg-ink/75 backdrop-blur-md border-b border-white/10"
    >
      <div data-testid="announcement-bar" className="bg-gold text-ink text-center text-[10px] md:text-xs tracking-[0.12em] uppercase py-2 px-4 font-medium">
        First order? 10% off with code FIRSTPHUDG · Free delivery · Bundle 2+ 1L bottles, save 12%
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <button
          data-testid="header-logo"
          onClick={() => go("/")}
          className="flex items-center"
          aria-label="PhudG home"
        >
          <img src="/images/phudg-logo-light.png" alt="PhudG" className="h-9 w-auto" />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.to}
              data-testid={l.testid}
              onClick={() => go(l.to)}
              className="text-sm font-light tracking-widest uppercase text-cream/70 hover:text-gold transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <Link to="/cart" data-testid="header-cart-btn" className="relative text-cream/70 hover:text-gold transition-colors duration-300" aria-label="Cart">
            <ShoppingCart size={20} />
            {count > 0 && (
              <span data-testid="header-cart-count" className="absolute -top-2 -right-2.5 flex h-4 w-4 items-center justify-center rounded-full bg-gold text-[10px] font-medium text-ink">
                {count}
              </span>
            )}
          </Link>
          <motion.button
            data-testid="header-where-to-buy-btn"
            onClick={() => go("/where-to-buy")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className="flex items-center gap-2 rounded-full bg-gold px-5 py-2 text-sm font-medium text-ink hover:bg-amberbrand hover:text-cream transition-colors duration-300"
          >
            <Store size={16} />
            Where to Buy
          </motion.button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden text-cream"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="md:hidden overflow-hidden bg-ink/95 backdrop-blur-md border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.to}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => go(l.to)}
                  className="text-left font-serif text-2xl text-cream/80 hover:text-gold transition-colors duration-300"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-link-faq"
                onClick={() => go("/faq")}
                className="text-left font-serif text-2xl text-cream/80 hover:text-gold transition-colors duration-300"
              >
                FAQ
              </button>
              <button
                data-testid="mobile-nav-link-contact"
                onClick={() => go("/contact")}
                className="text-left font-serif text-2xl text-cream/80 hover:text-gold transition-colors duration-300"
              >
                Contact
              </button>
              <button
                data-testid="mobile-where-to-buy-btn"
                onClick={() => go("/where-to-buy")}
                className="mt-2 flex w-fit items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-medium text-ink"
              >
                <Store size={16} />
                Where to Buy
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
